/* eslint-disable max-lines */
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from 'antd';

import { type generateBasicDataWithAIValidatorType } from '@eduzz-product-manager/domain';
import { useMutation } from '@tanstack/react-query';

import Ai from '@/components/icons/Ai';
import { type RouterOutput, trpcVanillaClient } from '@/lib/trpc';
import { productDetailsWithIaSteps, productDetailsWithoutIaSteps } from '@/pages/CreateProduct/steps';
import { aiProductExampleStore } from '@/stores/AIProductExampleStore';
import { productStore } from '@/stores/productStore';
import { stepStore } from '@/stores/steps';
import { useTestFlagsStore } from '@/stores/testFlags';

const subtitles = [
  'Descobrindo o segredo por trás das suas palavras.',
  'Caçando boas frases para destacar no anúncio.',
  'Quase lá! A mágica está acontecendo.',
  'Isso pode levar algum tempo, especialmente para arquivos grandes.',
  'A IA está trabalhando duro para entender seu conteúdo.'
];

const takingTooLongSubtitles = [
  'Isso está demorando mais do que o esperado, o que deseja fazer?',
  'A IA está enfrentando um grande volume de dados, deseja aguardar mais 20 segundos?',
  'Se preferir, podemos seguir sem a ajuda da IA e você pode preencher os dados manualmente.',
  'Aguarde mais um pouco ou continue sem a ajuda da IA?',
  'Paciência é uma virtude, mas entendemos se quiser seguir sem a IA.'
];

const TAKING_TOO_LONG_THRESHOLD_MS = 20000;

type GenerateBasicDataWithAIResponse = NonNullable<RouterOutput['deliverables']['generateBasicDataWithAI']>;

const ProductAiLoadingExamples = () => {
  const store = productStore.getState();
  const [startedAt, setStartedAt] = useState(Date.now());
  const [takingTooLong, setTakingTooLong] = useState(false);
  const [takingTooLongSubtitleIndex, setTakingTooLongSubtitleIndex] = useState(0);

  const testMode = useTestFlagsStore(state => state.aiDeliverablesErrorTestMode);

  const abortRef = useRef<null | AbortController>(null);

  const generateBasicDataWithAIMutation = useMutation<
    GenerateBasicDataWithAIResponse,
    Error,
    generateBasicDataWithAIValidatorType,
    void
  >({
    retry: false,
    mutationFn: async ({ deliverablesIds, testMode }) => {
      if (testMode) {
        await new Promise((_resolve, reject) => setTimeout(() => reject(new Error('Test Error')), 1_000));
      }

      abortRef.current = new AbortController();

      return trpcVanillaClient.deliverables.generateBasicDataWithAI.mutate(
        { deliverablesIds, testMode },
        {
          signal: abortRef.current.signal
        }
      );
    },
    onSuccess: data => {
      if (data.title.length === 0) {
        stepStore.getState().nextStep();
        return;
      }

      aiProductExampleStore.setState({
        title: data.title,
        description: data.description,
        categories: data.categories,
        images: data.images,
        lastUsedExampleOf: {
          title: 0,
          description: 0,
          categories: 0,
          images: 0
        }
      });

      productStore.getState().setFields({
        title: data.title[0],
        description: data.description[0],
        categories: data.categories[0],
        image: data.images[0]
      });

      store.setIaField('product-title', true);
      store.setIaField('product-description', true);
      store.setIaField('categories', true);
      store.setIaField('product-image', Boolean(data.images[0]));

      stepStore.getState().addSteps({ steps: productDetailsWithIaSteps, removeStepsOnReturn: true });
      stepStore.getState().nextStep();
    }
  });

  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (Date.now() - startedAt > TAKING_TOO_LONG_THRESHOLD_MS && !takingTooLong) {
      setTakingTooLong(true);
    }

    const currentText = subtitles[index];

    if (!isDeleting && subIndex === currentText.length) {
      const pause = setTimeout(() => {
        setIsDeleting(true);
      }, 3000);

      return () => clearTimeout(pause);
    }

    if (isDeleting && subIndex <= 0) {
      setIndex(prev => (prev + 1) % subtitles.length);
      setIsDeleting(false);

      return;
    }

    const typingSpeed = isDeleting ? 20 : 60;

    const timeout = setTimeout(() => {
      setSubIndex(prev => prev + (isDeleting ? -2 : 1));
    }, typingSpeed);

    return () => clearTimeout(timeout);
  }, [subIndex, isDeleting, index, startedAt, takingTooLong]);

  useEffect(() => {
    return () => {
      // Cancel mutation on unmount
      abortRef?.current?.abort();
    };
  }, []);

  useEffect(() => {
    const unsub = productStore.subscribe((state, prevState) => {
      if (state.deliverables === prevState.deliverables) {
        return;
      }

      abortRef?.current?.abort();
      generateBasicDataWithAIMutation.reset();
    });

    return unsub;
  }, [generateBasicDataWithAIMutation]);

  const generateAiBasicData = useCallback(async () => {
    abortRef?.current?.abort();

    const state = productStore.getState();

    const deliverablesIds = state.deliverables
      .map(d => d.deliverableId)
      .filter(deliverableId => !!deliverableId)
      .map(String);

    if (deliverablesIds.length === 0) {
      stepStore.getState().nextStep();

      return;
    }

    if (generateBasicDataWithAIMutation.isPending || generateBasicDataWithAIMutation.isSuccess) {
      return;
    }

    await generateBasicDataWithAIMutation.mutateAsync({
      deliverablesIds,
      testMode
    });
  }, [generateBasicDataWithAIMutation, testMode]);

  useEffect(() => {
    if (
      generateBasicDataWithAIMutation.isPending ||
      generateBasicDataWithAIMutation.isSuccess ||
      generateBasicDataWithAIMutation.isError
    ) {
      return;
    }

    generateAiBasicData();
  }, [
    generateAiBasicData,
    generateBasicDataWithAIMutation.isPending,
    generateBasicDataWithAIMutation.isSuccess,
    generateBasicDataWithAIMutation.isError
  ]);

  const onSkipClick = () => {
    stepStore.getState().addSteps({ steps: productDetailsWithoutIaSteps, removeStepsOnReturn: true });
    stepStore.getState().nextStep();
  };

  return (
    <div className='step-wrapper-no-padding relative flex h-full w-full flex-1 flex-col overflow-auto bg-linear-to-r from-[#7B5DE6]/30 via-[#DE8FE9]/30 to-[#FBB5C0]/80 bg-cover dark:from-[#7B5DE6]/80 dark:via-[#DE8FE9]/80 dark:to-[#FBB5C0]/80'>
      <div className='relative bottom-0 flex flex-col self-center px-6 pt-16'>
        <div className='my-8 flex flex-col items-center'>
          <Ai className='animate-pulse-size w-10' />
        </div>

        {generateBasicDataWithAIMutation.isPending && (
          <div>
            <div className='flex flex-col space-y-4'>
              <div className='text-primary dark:text-content-title text-center text-2xl font-bold'>
                Nossa IA está analisando seu conteúdo...
              </div>

              <div className='text-center text-sm font-semibold'>
                {subtitles[index].substring(0, subIndex)}
                <span className='animate-blink'>|</span>
              </div>
            </div>

            <div className='my-14 flex flex-col space-y-5 opacity-90'>
              <div className='animate-shimmer relative h-5 w-[54%] rounded-xl bg-[linear-gradient(90deg,#C0CCE8_25%,#D4E0F0_50%,#C0CCE8_80%)]' />
              <div className='animate-shimmer relative left-[34%] h-5 w-[66%] rounded-xl bg-[linear-gradient(90deg,#C0CCE8_25%,#D4E0F0_50%,#C0CCE8_75%)]' />
              <div className='animate-shimmer-delayed relative h-5 w-[46%] rounded-xl bg-[linear-gradient(90deg,#C0CCE8_25%,#D4E0F0_50%,#C0CCE8_80%)]' />
              <div className='animate-shimmer-delayed relative left-[42%] h-5 w-[58%] rounded-xl bg-[linear-gradient(90deg,#C0CCE8_25%,#D4E0F0_50%,#C0CCE8_75%)]' />
            </div>
          </div>
        )}

        {takingTooLong && generateBasicDataWithAIMutation.isPending && (
          <div className='flex flex-col items-center space-y-4'>
            <div className='text-center text-lg font-semibold'>
              {takingTooLongSubtitles[takingTooLongSubtitleIndex]}
            </div>

            <div className='flex w-full flex-col gap-2 space-y-2'>
              <Button
                type='default'
                className='w-full rounded-lg!'
                id='button-wait-for-ai-to-finish'
                onClick={() => {
                  setTakingTooLongSubtitleIndex((takingTooLongSubtitleIndex + 1) % takingTooLongSubtitles.length);
                  setTakingTooLong(false);
                  setStartedAt(Date.now());
                }}
              >
                Esperar a IA terminar
              </Button>

              <Button
                type='text'
                className='w-full rounded-lg!'
                id='button-follow-without-ai-help'
                onClick={onSkipClick}
              >
                Seguir sem ajuda da IA
              </Button>
            </div>
          </div>
        )}

        {generateBasicDataWithAIMutation.isError && (
          <>
            <div className='flex flex-col space-y-5 opacity-90'>
              <div className='text-primary dark:text-content-title text-center text-2xl font-bold'>
                Nossa IA não conseguiu ler seu arquivo.
              </div>

              <div className='text-center text-sm font-semibold'>
                Tudo bem, pode ser apenas o tamanho do arquivo. Você pode continuar e ainda assim receber ajuda da IA
                depois, se precisar.
              </div>

              <div className='flex w-full flex-col gap-2 space-y-2'>
                <Button
                  id='button-retry-ai'
                  type='primary'
                  className='w-full rounded-lg!'
                  onClick={generateAiBasicData}
                >
                  Tentar novamente
                </Button>

                <Button id='button-skip-ai' type='default' className='w-full rounded-lg!' onClick={onSkipClick}>
                  Seguir sem ajuda da IA
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductAiLoadingExamples;
